import React, {
  ChangeEvent,
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";

import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import { Input } from "antd";
import { SearchProps } from "antd/es/input";
import { InputRef } from "antd/es/input/Input";
import cn from "classnames";

import ErrorRow from "../input/ErrorRow";
import Labeled, { LabeledFieldProps } from "../input/Labeled";

const block = bem("mmc-search-field");

export interface SearchFieldProps
  extends Omit<SearchProps, "onChange" | "size">,
    Omit<LabeledFieldProps, "children"> {
  className?: string;
  error?: ReactNode;
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  showEnterButton?: boolean;
  size?: Exclude<SearchProps["size"], "small">;
}

const SearchField = forwardRef<SearchFieldComponent, SearchFieldProps>(
  (
    {
      addonAfter,
      className,
      enterButton = true,
      error,
      label,
      labelClassName,
      labelPosition,
      onChange,
      rowProps,
      showEnterButton = false,
      sideLabelSpan,
      size = "middle",
      ...props
    },
    ref
  ) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => onChange && onChange(event.target.value, event),
      [onChange]
    );

    const inputRef = useRef<InputRef>(null);
    useImperativeHandle(ref, () => ({
      blur: () => inputRef.current?.blur(),
      focus: () => inputRef.current?.focus(),
    }));

    return (
      <Labeled
        className={cn(block({ [size]: true }), className)}
        label={label}
        labelClassName={labelClassName}
        labelPosition={labelPosition}
        rowProps={rowProps}
        sideLabelSpan={sideLabelSpan}
      >
        {showEnterButton ? (
          <Input.Search
            enterButton={enterButton}
            onChange={handleChange}
            ref={inputRef}
            size={size}
            {...props}
          />
        ) : (
          <Input
            addonAfter={addonAfter}
            onChange={handleChange}
            ref={inputRef}
            size={size}
            suffix={<FontAwesomeIcon className={block("search-icon")} icon={faSearch} />}
            {...props}
          />
        )}
        {error && <ErrorRow>{error}</ErrorRow>}
      </Labeled>
    );
  }
);

export interface SearchFieldComponent {
  blur(): void;
  focus: () => void;
}

export default SearchField;
